import React, { createContext, useContext, useState, useEffect } from "react";
import { workbookCache } from "../../excel/workbook/cache";
import { getShowDataSheetsState, hasDataTableWorksheets, initializeShowDataSheetsState } from "../../excel/workbook";
import type { UserSettings } from "../../interfaces/UserSettings";

interface UserSettingsContextType {
  settings: UserSettings;
  /**
   * Resolves once the settings have been read from the workbook, with whether it has data tables
   * (true if they couldn't be read). Workbook work at startup waits on it, so the two don't
   * overlap.
   */
  settingsLoaded: Promise<boolean>;
  updateSettings: (newSettings: Partial<UserSettings>) => Promise<void>;
  setHasDataSheets: (value: boolean) => void;
}

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

export const UserSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<UserSettings>({
    showDataSheets: false,
    useEdiphiColors: true,
    hasDataSheets: false,
  });
  const [loaded] = useState(() => {
    let resolve: (hasDataTables: boolean) => void = () => {};
    const promise = new Promise<boolean>((done) => (resolve = done));
    return { promise, resolve };
  });

  useEffect(() => {
    const initializeSettings = async () => {
      // workbookCache.get and hasDataTableWorksheets each open their own Excel.run; Office.js can't
      // run them concurrently, so await them in sequence.
      const savedUseEdiphiColors = await workbookCache.get("useEdiphiColors");
      const dataTablesPresent = await hasDataTableWorksheets();
      await initializeShowDataSheetsState();

      setSettings({
        showDataSheets: getShowDataSheetsState(),
        useEdiphiColors: savedUseEdiphiColors === null ? true : savedUseEdiphiColors,
        hasDataSheets: dataTablesPresent,
      });
      return dataTablesPresent;
    };

    initializeSettings().then(loaded.resolve, () => loaded.resolve(true));
  }, [loaded]);

  const updateSettings = async (newSettings: Partial<UserSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);

    if (newSettings.useEdiphiColors !== undefined) {
      await workbookCache.set({ useEdiphiColors: newSettings.useEdiphiColors });
    }
  };

  const setHasDataSheets = (value: boolean) => {
    setSettings((prev) => ({ ...prev, hasDataSheets: value }));
  };

  return (
    <UserSettingsContext.Provider
      value={{ settings, settingsLoaded: loaded.promise, updateSettings, setHasDataSheets }}
    >
      {children}
    </UserSettingsContext.Provider>
  );
};

export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (context === undefined) {
    throw new Error("useUserSettings must be used within a UserSettingsProvider");
  }
  return context;
};
